import React, { useEffect, useState } from 'react';
import LabeledField from '../../InquiryRotationView/InquirySchemeManagement/components/LabeledField';
import { Button } from '@material-ui/core';
import {
  CheckboxesComponent,
  DatePickerComponent,
  Inputs,
} from '../../../../Components';
import { PaymentPlansLocalePath } from '../i18n/PaymentPlansLocale';
import { useTranslation } from 'react-i18next';
import { Add } from '@material-ui/icons';
import { PropertyPaymentPlan } from '../../../../Enums';
import Joi from 'joi';
import {
  getErrorByName,
  showError,
} from '../../../../Helper';
import moment from 'moment';
import { config } from '../../../../config';
import LookupAutocomplete from '../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import { useKeyGenerator } from '../../../../Hooks/keyGenerator';
import { GetLookupItemsByLookupTypeName } from '../../../../Services';
import { frequencyTypes } from '../constants';

const initialInstallment = (frequency) => ({
  installmentTypeId: config.InstallmentTypes.Installment,
  amountPercentage: '',
  frequencyId: frequency?.lookupItemId,
  isBasedOnBookingDate: false,
  frequencyValue: '',
  frequencyName: frequency?.lookupItemName.toLowerCase(),
  numberOfInstallment: null,
  id: crypto.randomUUID(),
});

// const frequencyTypes = {
//   month: 'month',
//   date: 'date',
//   constructionProgress: 'construction progress',
// };
const InstallmentItem = ({ dispatch }) => {
  const { t } = useTranslation(PaymentPlansLocalePath);
  const [installment, setInstallment] = useState(initialInstallment);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [monthlyFrequency, setMonthlyFrequency] = useState(null);
  const [key, refresh] = useKeyGenerator();

  const updateInstallmentHandler = (field, value) => {
    if (field === 'edit-some') {
      setInstallment((prevInstallment) => ({
        ...prevInstallment,
        ...value,
      }));
      return;
    }
    setInstallment((prevInstallment) => ({
      ...prevInstallment,
      [field]: value,
    }));
  };
  const installmentSchema = Joi.object({
    installmentTypeId: Joi.number().required(),
    amountPercentage: Joi.number()
      .required()
      .min(1)
      .max(99)
      .messages({
        'number.min': t('installmentAmountPercentageMin'),
        'number.max': t('installmentAmountPercentageMax'),
        'number.base': t('field_is_required', {
          field: t('amount_percentage'),
          ns: 'Shared',
        }),
      }),
    frequencyId: Joi.number()
      .required()
      .messages({
        'number.base': t('field_is_required', {
          field: t('trigger_type'),
          ns: 'Shared',
        }),
        'number.empty': t('field_is_required', {
          field: t('trigger_type'),
          ns: 'Shared',
        }),
        'any.required': t('field_is_required', {
          field: t('trigger_type'),
          ns: 'Shared',
        }),
      }),
    frequencyValue: Joi.string()
      .required()
      .custom((value, helper) => {
        if (+value) {
          if (!Number.isInteger(+value)) {
            return helper.error('string.decimal');
          } else if (+value < 1) {
            return helper.error('string.negative');
          }
        }
      })
      .messages({
        'string.required': t('field_is_required', {
          field: t('trigger_value'),
          ns: 'Shared',
        }),
        'string.base': t('field_is_required', {
          field: t('trigger_value'),
          ns: 'Shared',
        }),
        'string.empty': t('field_is_required', {
          field: t('trigger_value'),
          ns: 'Shared',
        }),
        'string.negative': t('valueMustBePositive'),
        'string.decimal': t('string_decimal'),
      }),

    numberOfInstallment: Joi.when('frequencyName', {
      is: frequencyTypes.MONTH,
      then: Joi.alternatives().conditional('isBasedOnBookingDate', {
        is: true,
        then: Joi.optional(),
        otherwise: Joi.number()
          .min(1)
          .label(t('number_of_installment'))
          .required()
          .messages({
            'number.min': t('number_of_installment_min'),
            'any.required': t('field_is_required', {
              field: t('number_of_installment'),
              ns: 'Shared',
            }),
          }),
      }),
      otherwise: Joi.optional(),
    }),
  }).validate(installment, { allowUnknown: true, abortEarly: false });

  const onAddHandler = () => {
    setIsSubmitted(true);
    if (installmentSchema.error) {
      return showError(t('please-fill-all-required-field', { ns: 'Shared' }));
    }
    installment.amountPercentage = +installment.amountPercentage / 100;
    dispatch({ field: 'installment', value: installment });
    setInstallment(initialInstallment(monthlyFrequency));
    setIsSubmitted(false);
    refresh();
  };
  useEffect(() => {
    const getFrequencies = async () => {
      try {
        const res = await GetLookupItemsByLookupTypeName({
          lookUpName: PropertyPaymentPlan.Frequency.lookupTypeName,
          pageIndex: 1,
          pageSize: 100,
        });
        const monthFrequency = res.result.find(
          (item) => item.lookupItemName.toLowerCase() === frequencyTypes.MONTH
        );
        setMonthlyFrequency(monthFrequency);
        updateInstallmentHandler('edit-some', {
          frequencyId: monthFrequency?.lookupItemId,
          frequencyName: monthFrequency?.lookupItemName.toLowerCase(),
        });
      } catch (error) {
        showError('error');
      }
    };
    getFrequencies();
  }, []);

  return (
    <div key={key}>
      <LabeledField
        label={t('installment')}
        className='gap-32'
        component={
          <div className='flex gap-1'>
            <CheckboxesComponent
              label={t('installment_based_on_booking_date')}
              onSelectedCheckboxChanged={(e) => {
                updateInstallmentHandler('edit-some', {
                  isBasedOnBookingDate: e.target.checked,
                  frequencyName: frequencyTypes.MONTH,
                  frequencyId: monthlyFrequency?.lookupItemId,
                });
              }}
              singleChecked={installment.isBasedOnBookingDate}
            />
            <Inputs
              type={'number'}
              startAdornment={<span className='start-adornment'>%</span>}
              inputPlaceholder={'Enter installment percentage (e.g., 25%)'}
              wrapperClasses={'px-2'}
              onInputChanged={(e) =>
                updateInstallmentHandler('amountPercentage', e.target.value)
              }
              value={installment.amountPercentage}
              isWithError
              helperText={
                getErrorByName(installmentSchema, 'amountPercentage').message
              }
              error={
                getErrorByName(installmentSchema, 'amountPercentage').error
              }
              isSubmitted={isSubmitted}
            />
          </div>
        }
      />
      {installment.isBasedOnBookingDate ? (
        <LabeledField
          hideDivider
          className='gap-32'
          component={
            <div className='flex- flex-col gap-6'>
              <Inputs
                type='number'
                inputPlaceholder={'Enter months (e.g., 12)'}
                startAdornment={
                  <span className='start-adornment'>{t('months')}</span>
                }
                value={installment.frequencyValue}
                onInputChanged={(e) =>
                  updateInstallmentHandler(
                    'frequencyValue',
                    e.currentTarget.value
                  )
                }
                isWithError
                helperText={
                  getErrorByName(installmentSchema, 'frequencyValue').message
                }
                error={
                  getErrorByName(installmentSchema, 'frequencyValue').error
                }
                isSubmitted={isSubmitted}
              />
              <span className='helper-text'>{t('by_booking_date_text')}</span>
            </div>
          }
        />
      ) : (
        <>
          <LabeledField
            className='gap-32'
            hideDivider
            label={t('trigger_type')}
            component={
              <LookupAutocomplete
                filterOptions={(options) =>
                  options.filter((item) =>
                    Object.values(frequencyTypes).includes(
                      item.lookupItemName.toLowerCase()
                    )
                  )
                }
                wrapperClasses={'py-3'}
                isSubmitted={isSubmitted}
                withError
                error={getErrorByName(installmentSchema, 'frequencyId').error}
                helperText={
                  getErrorByName(installmentSchema, 'frequencyId').message
                }
                lookupName={PropertyPaymentPlan.Frequency.lookupTypeName}
                selectedValues={
                  installment.frequencyId
                    ? {
                        lookupItemId: installment.frequencyId,
                        lookupItemName: installment.frequencyName,
                      }
                    : null
                }
                onChange={(_e, newValue) => {
                  updateInstallmentHandler('edit-some', {
                    frequencyId: newValue?.lookupItemId,
                    frequencyName: newValue?.lookupItemName.toLowerCase(),
                  });
                }}
              />
            }
          />
          {installment.frequencyName === frequencyTypes.MONTH && (
            <>
              <LabeledField
                hideDivider
                className='gap-32'
                label={t('number_of_installment')}
                component={
                  <Inputs
                    type='number'
                    inputPlaceholder={'Enter (e.g., 10)'}
                    value={installment.numberOfInstallment}
                    onInputChanged={(e) =>
                      updateInstallmentHandler(
                        'numberOfInstallment',
                        e.currentTarget.value
                      )
                    }
                    isWithError
                    helperText={
                      getErrorByName(installmentSchema, 'numberOfInstallment')
                        .message
                    }
                    error={
                      getErrorByName(installmentSchema, 'numberOfInstallment')
                        .error
                    }
                    isSubmitted={isSubmitted}
                  />
                }
              />
              <LabeledField
                hideDivider
                className='gap-32'
                label={t('frequency')}
                component={
                  <div className='flex- flex-col gap-6'>
                    <Inputs
                      type='number'
                      inputPlaceholder={'(e.g., every 1, 2 months)'}
                      startAdornment={
                        <span className='start-adornment'>{t('months')}</span>
                      }
                      value={installment.frequencyValue}
                      onInputChanged={(e) =>
                        updateInstallmentHandler(
                          'frequencyValue',
                          e.currentTarget.value
                        )
                      }
                      isWithError
                      helperText={
                        getErrorByName(installmentSchema, 'frequencyValue')
                          .message
                      }
                      error={
                        getErrorByName(installmentSchema, 'frequencyValue')
                          .error
                      }
                      isSubmitted={isSubmitted}
                    />
                    <span className='helper-text'>{t('by_month_text')}</span>
                  </div>
                }
              />
            </>
          )}
          {installment.frequencyName === frequencyTypes.DATE && (
            <LabeledField
              className='gap-32'
              label={t('by_fixed_date')}
              hideDivider
              component={
                <div className='flex flex-col gap-6'>
                  <DatePickerComponent
                    onDateChanged={(e) =>
                      updateInstallmentHandler(
                        'frequencyValue',
                        new Date(e._d).toLocaleDateString()
                      )
                    }
                    value={moment(installment.frequencyValue)}
                    helperText={
                      getErrorByName(installmentSchema, 'frequencyValue')
                        .message
                    }
                    error={
                      getErrorByName(installmentSchema, 'frequencyValue').error
                    }
                    isSubmitted={isSubmitted}
                  />
                  <span className='helper-text'>
                    {t('select_installment_date')}
                  </span>
                </div>
              }
            />
          )}
        </>
      )}
      {installment.frequencyName === frequencyTypes.CONSTRUCTION_PROGRESS && (
        <LabeledField
          className='gap-32'
          label={t('construction_progress')}
          hideDivider
          component={
            <div className='flex flex-col gap-6'>
              <Inputs
                type='number'
                value={installment.frequencyValue}
                onInputChanged={(e) =>
                  updateInstallmentHandler('frequencyValue', e.target.value)
                }
                startAdornment={<span className='start-adornment'>%</span>}
                isWithError
                helperText={
                  getErrorByName(installmentSchema, 'frequencyValue').message
                }
                error={
                  getErrorByName(installmentSchema, 'frequencyValue').error
                }
                isSubmitted={isSubmitted}
              />
              <span className='helper-text'>
                {t('inset_percentage_of_construction_progress')}
              </span>
            </div>
          }
        />
      )}
      <LabeledField
        hideDivider
        className='gap-32'
        component={
          <Button className=' capitalize flex gap-2' onClick={onAddHandler}>
            {' '}
            <Add />
            <span>{t('add_another')}</span>
          </Button>
        }
      />
    </div>
  );
};

export default InstallmentItem;
